import { InboundEmailDTO } from './InboundEmailDTO';
import { SalesOrderSource, StagedPurchaseOrderStatus } from '@erp/shared-types';
import type {
  DiscountValidationResult as RpcDiscountValidationResult,
  CustomerSpecificDiscountVerificationStatus as RpcCustomerSpecificDiscountVerificationStatus,
} from '@erp/rpc';
import { DiscountStatus as RpcDiscountStatus } from '@erp/rpc';

// Re-export discount validation types from RPC for single source of truth
export type DiscountValidationResult = RpcDiscountValidationResult;
export type DiscountStatus = RpcDiscountStatus;
export const DiscountStatus = RpcDiscountStatus;

// Re-export customer-specific discount verification types
export type CustomerSpecificDiscountVerificationStatus =
  RpcCustomerSpecificDiscountVerificationStatus;

/**
 * Customer-specific discount verification tracking for a line item.
 * Stored in LineItem JSONB within StagedCustomerPurchaseOrder.
 */
export interface CustomerSpecificDiscountVerification {
  discountId: string;
  discountScopeId: string;
  endCustomerName: string;
  status: CustomerSpecificDiscountVerificationStatus;
  verifiedByUserId?: string;
  verifiedAt?: string; // ISO date string
  notes?: string;
}

// Re-export for backward compatibility with existing frontend code
export { SalesOrderSource };
export type SalesOrderDraftStatus = StagedPurchaseOrderStatus;
export const SalesOrderDraftStatus = StagedPurchaseOrderStatus;

export interface Charge {
  id?: string;
  type: 'shipping' | 'handling' | 'tax' | 'discount' | 'other';
  description: string;
  amount: number;
  isPercentage: boolean; // If true, amount is a percentage; if false, it's a fixed amount
}

export enum InventoryStatus {
  /** Available quantity meets or exceeds requested quantity */
  SUFFICIENT = 'SUFFICIENT',
  /** Available quantity is less than requested quantity */
  INSUFFICIENT = 'INSUFFICIENT',
  /** Unable to determine inventory status */
  UNKNOWN = 'UNKNOWN',
}

export interface InventoryValidationResult {
  /** Status of the inventory check */
  status: InventoryStatus;
  /** Quantity requested on the line item */
  requestedQuantity: number;
  /** Available quantity (on-hand minus reserved), null if unknown */
  availableQuantity: number | null;
  /** Total on-hand quantity, null if unknown */
  onhandQuantity: number | null;
  /** Reserved quantity, null if unknown */
  reservedQuantity: number | null;
  /** Organization code used for the query */
  organizationCode: string | null;
  /** Item number/SKU used for the query */
  itemNumber: string | null;
  /** Error message if the check failed */
  errorMessage?: string;
}

export interface LineItem {
  // Basic line item information
  origin?: string;
  unit?: string;
  pallets?: number;
  quantity?: number;
  unitPrice?: number;
  amount?: number;

  //deprecated
  sku?: string;
  itemName?: string;
  name?: string;
  upc?: string;

  // email fields
  customerDescription?: string;
  customerIdentifier?: string;

  // AI suggested values for accuracy tracking
  suggestedQuantity?: number;
  suggestedUnitPrice?: number;
  suggestedAmount?: number;
  suggestedCustomerDescription?: string;
  suggestedCustomerIdentifier?: string;

  // edi
  rescheduledDate?: string;
  ediRejected?: boolean;

  suggestedProductVariantId?: string;
  suggestedProductId?: string;
  selectedProductVariantId?: string;
  topProductVariantMatches?: Array<{
    productVariantId: string;
    confidence: number;
  }>;
  selectedCustomerProductId?: string;
  suggestedCustomerProductId?: string;

  // Discount validation
  discountValidation?: RpcDiscountValidationResult;

  // Inventory validation
  inventoryValidation?: InventoryValidationResult;

  // Line item charges
  charges?: Charge[];

  // Customer-specific discount verification tracking
  customerSpecificDiscountVerification?: CustomerSpecificDiscountVerification;

  PO1_loop?: {
    [key: string]: unknown;
    baselineItemData: {
      quantity: string;
      unitPrice: string;
      productServiceID: string;
      productServiceID1: string;
      productServiceID2: string;
      productServiceID3: string;
      productServiceID4: string;
      productServiceID5: string;
      productServiceIDQualifier: string;
      productServiceIDQualifier1: string;
      productServiceIDQualifier2: string;
      productServiceIDQualifier3: string;
      productServiceIDQualifier4: string;
      productServiceIDQualifier5: string;
      unitOrBasisForMeasurementCode: string;
    };
  };
}

export interface SalesOrderDraft {
  id: string;
  organizationId: string;
  status: SalesOrderDraftStatus;
  source: SalesOrderSource;
  sourceId: string;
  /** Whether this order meets all criteria for approval */
  isReadyForApproval?: boolean;
  sourceData?: {
    email?: InboundEmailDTO;
    orderfulJson?: Record<string, unknown>;
    document?: {
      id: string;
      originalFileName: string;
      mimeType: string;
      fileSizeBytes: number;
      url: string;
    };
  };
  sourceEmail?: InboundEmailDTO;
  suggestedCustomerId?: string;
  suggestedCustomer?: {
    id: string;
    name: string;
    address?: string;
    phoneNumber?: string;
    emailDomain?: string;
    contactPerson?: string;
    creditLimit?: number;
  };
  suggestedCustomerRejected: boolean;
  customerId?: string;
  customer?: {
    id: string;
    name: string;
    erpDisplayName?: string | null;
    address?: string;
    phoneNumber?: string;
    emailDomain?: string;
    contactPerson?: string;
    creditLimit?: number;
  };
  suggestedCustomerCreationRejected: boolean;
  purchaseOrderNumber: string;
  receivedDate: Date | string;
  orderDate: Date | string;
  deliveryDate?: Date | string;
  shipDate?: Date | string;
  totalAmount?: string;
  extractedTotalAmount?: number;
  currency?: string;

  // Order-level charges
  charges?: Charge[];

  // Memory bank notes are now fetched via RPC (removed from DTO)

  destinationAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  billToAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  buyingPartyAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  lineItems?: Array<LineItem>;
  rawData: {
    extractedData: unknown;
    email?: {
      id: string;
      subject: string;
      from: string;
    };
    attachmentIds: string[];
  };
  shipToLocationId?: string;
  billToLocationId?: string;
  buyingPartyLocationId?: string;
  // AI suggestion reference
  currentAiSuggestionId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}
